import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { TASK_TYPES, TASK_TYPE_LABELS } from '@claimup/shared/constants/assignments';
import { AI_ACTION_TYPE_VALUES, AI_ACTION_LABELS } from '@claimup/shared/constants/ai-agent';
import { baseAPI } from '../../../config';
import { companyLabel } from '../../../utils/companyLabel';

const AI_PROVIDERS = ['openai', 'anthropic'];

// 🔴 Sale del catalogo compartido, NO de una lista a mano.
//
// La que habia aqui escrita se quedo vieja: `cancel_assignment` se anadio al
// backend y nunca llego a esta pantalla, asi que no se podia marcar. Un prompt
// guardado desde aqui nacia con una lista blanca sin ella, el modelo no veia la
// herramienta y el agente no cancelaba tareas nunca — sin dar ningun error, y
// pareciendo que no hacia caso al prompt.
const TOOLS = AI_ACTION_TYPE_VALUES.map(value => ({ value, label: AI_ACTION_LABELS[value] || value }));

function buildFormData(prompt, defaultCompanyId) {
  return {
    task_type: prompt?.task_type || '',
    business_line: prompt?.business_line || null,
    // client_id: empresa a la que aplica el prompt ('' = genérico/todas). En
    // edición viene del prompt; en creación puede venir preseleccionado.
    client_id: prompt?.client_id || defaultCompanyId || '',
    prompt_text: prompt?.prompt_text || '',
    description: prompt?.description || '',
    ai_provider: prompt?.ai_provider || 'openai',
    model_id: prompt?.model_id || 'gpt-4o',
    max_tokens: prompt?.max_tokens || 2048,
    tools_enabled: prompt?.tools_enabled || [],
    allTools: !prompt?.tools_enabled || prompt?.tools_enabled?.length === 0,
    interactions_limit: prompt?.context_config?.interactions_limit || '',
    include_annotations: prompt?.context_config?.include_annotations || false,
    include_documents: prompt?.context_config?.include_documents || false,
    memoria_de_casos: prompt?.context_config?.memoria_de_casos || 'off',
  };
}

const AIAgentPromptForm = ({ prompt, modalOpen, onClose, companies = [], defaultCompanyId }) => {
  const isEditing = !!prompt;
  const modalRef = useRef(null);

  const [formData, setFormData] = useState(() => buildFormData(prompt, defaultCompanyId));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (modalOpen) {
      setFormData(buildFormData(prompt, defaultCompanyId));
      setError('');
      setSaving(false);
    }
  }, [modalOpen, prompt, defaultCompanyId]);

  // Close on ESC
  useEffect(() => {
    if (!modalOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [modalOpen, onClose]);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleToolToggle = (toolValue) => {
    setFormData(prev => {
      const current = prev.tools_enabled || [];
      const updated = current.includes(toolValue)
        ? current.filter(t => t !== toolValue)
        : [...current, toolValue];
      return { ...prev, tools_enabled: updated };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.task_type) {
      setError('El tipo de tarea es obligatorio');
      return;
    }
    if (!formData.prompt_text.trim()) {
      setError('El texto del prompt es obligatorio');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        task_type: formData.task_type,
        business_line: formData.business_line || null,
        prompt_text: formData.prompt_text,
        description: formData.description,
        ai_provider: formData.ai_provider,
        model_id: formData.model_id,
        max_tokens: parseInt(formData.max_tokens) || 2048,
        client_id: formData.client_id || null,
        tools_enabled: formData.allTools ? null : formData.tools_enabled,
        // ⚠️ Este objeto se manda ENTERO y pisa el que hubiera. Cualquier ajuste
        // del contexto que no esté en este formulario se borra al guardar: es lo
        // que pasó con `memoria_de_casos` el 21/09/2026, que vivía en el backend
        // y la pantalla lo destruía sin decir nada.
        context_config: {
          interactions_limit: formData.interactions_limit ? parseInt(formData.interactions_limit) : null,
          include_annotations: formData.include_annotations,
          include_documents: formData.include_documents,
          memoria_de_casos: formData.memoria_de_casos,
        },
      };

      if (isEditing) {
        await axios.put(`${baseAPI}/ai-system-prompts/${prompt._id}`, payload, { headers });
      } else {
        await axios.post(`${baseAPI}/ai-system-prompts`, payload, { headers });
      }

      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar el prompt');
    } finally {
      setSaving(false);
    }
  };

  if (!modalOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className='fixed inset-0 bg-gray-900 bg-opacity-30 z-50' onClick={onClose} />

      {/* Modal */}
      <div className='fixed inset-0 z-50 overflow-hidden flex items-center justify-center px-4'>
        <div
          ref={modalRef}
          className='bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-auto'
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className='px-5 py-3 border-b border-gray-200 flex justify-between items-center'>
            <div className='font-semibold text-gray-800'>
              {isEditing ? `Editar prompt (v${prompt.version})` : 'Nuevo prompt'}
            </div>
            <button className='text-gray-400 hover:text-gray-500' onClick={onClose}>
              <svg className='w-4 h-4 fill-current'>
                <path d='M7.95 6.536l4.242-4.243a1 1 0 111.415 1.414L9.364 7.95l4.243 4.242a1 1 0 11-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 01-1.414-1.415L6.536 7.95 2.293 3.707a1 1 0 011.414-1.414L7.95 6.536z' />
              </svg>
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className='px-5 py-4 space-y-4'>
            {error && (
              <div className='text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2'>{error}</div>
            )}

            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-slate-700 mb-1'>Tipo de tarea *</label>
                <select
                  value={formData.task_type}
                  onChange={e => handleChange('task_type', e.target.value)}
                  className='w-full text-sm border border-slate-200 rounded-lg px-3 py-2'
                  disabled={isEditing}
                >
                  <option value=''>Seleccionar...</option>
                  {TASK_TYPES.map(t => (
                    <option key={t} value={t}>{TASK_TYPE_LABELS[t] || t}</option>
                  ))}
                </select>
              </div>
              <div>
                {/* La línea del expediente. Vacío = sirve para las dos, que es
                    lo que eran todos los prompts hasta ahora.

                    En AR hace falta elegirla: el resolver NO cae al genérico
                    para esa línea, porque un prompt de recobro le pide al
                    modelo redactar texto libre y por el canal oficial de Meta
                    eso no se puede enviar. */}
                <label className='block text-sm font-medium text-slate-700 mb-1'>
                  Línea de negocio
                </label>
                <select
                  value={formData.business_line || ''}
                  onChange={e => handleChange('business_line', e.target.value || null)}
                  className='w-full text-sm border border-slate-200 rounded-lg px-3 py-2'
                >
                  <option value=''>Las dos</option>
                  <option value='account-receivable'>Solo gestión propia</option>
                  <option value='extra-judicial'>Solo recobro extrajudicial</option>
                </select>
                {formData.business_line === 'account-receivable' && (
                  <p className='mt-1 text-xs text-amber-700'>
                    En AR el modelo no puede redactar WhatsApp: quítale{' '}
                    <code>send_whatsapp</code> y dale <code>send_whatsapp_template</code>.
                  </p>
                )}
              </div>
              <div>
                <label className='block text-sm font-medium text-slate-700 mb-1'>Descripcion</label>
                <input
                  type='text'
                  value={formData.description}
                  onChange={e => handleChange('description', e.target.value)}
                  className='w-full text-sm border border-slate-200 rounded-lg px-3 py-2'
                  placeholder='Descripcion breve del prompt'
                />
              </div>
            </div>

            <div>
              <label className='block text-sm font-medium text-slate-700 mb-1'>Empresa</label>
              <select
                value={formData.client_id}
                onChange={e => handleChange('client_id', e.target.value)}
                className='w-full text-sm border border-slate-200 rounded-lg px-3 py-2 disabled:bg-slate-50 disabled:text-slate-500'
                disabled={isEditing}
              >
                <option value=''>Genérico (todas las empresas)</option>
                {companies.map(c => (
                  <option key={c._id} value={c._id}>{companyLabel(c)}</option>
                ))}
              </select>
              <p className='mt-1 text-xs text-slate-400'>
                {isEditing
                  ? 'La empresa de un prompt no se cambia al editar; crea uno nuevo si necesitas otra.'
                  : 'Si eliges una empresa, este prompt solo aplica a ella (sobrescribe el genérico para ese tipo de tarea).'}
              </p>
            </div>

            <div>
              <label className='block text-sm font-medium text-slate-700 mb-1'>Texto del prompt *</label>
              <textarea
                value={formData.prompt_text}
                onChange={e => handleChange('prompt_text', e.target.value)}
                className='w-full text-sm border border-slate-200 rounded-lg px-3 py-2 font-mono'
                rows={12}
                placeholder='Instrucciones para el LLM...'
              />
            </div>

            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
              <div>
                <label className='block text-sm font-medium text-slate-700 mb-1'>Provider</label>
                <select
                  value={formData.ai_provider}
                  onChange={e => handleChange('ai_provider', e.target.value)}
                  className='w-full text-sm border border-slate-200 rounded-lg px-3 py-2'
                >
                  {AI_PROVIDERS.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className='block text-sm font-medium text-slate-700 mb-1'>Modelo</label>
                <input
                  type='text'
                  value={formData.model_id}
                  onChange={e => handleChange('model_id', e.target.value)}
                  className='w-full text-sm border border-slate-200 rounded-lg px-3 py-2'
                  placeholder='gpt-4o'
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-slate-700 mb-1'>Max tokens</label>
                <input
                  type='number'
                  value={formData.max_tokens}
                  onChange={e => handleChange('max_tokens', e.target.value)}
                  className='w-full text-sm border border-slate-200 rounded-lg px-3 py-2'
                  placeholder='2048'
                />
              </div>
            </div>

            <div>
              <label className='block text-sm font-medium text-slate-700 mb-2'>Herramientas disponibles</label>
              <label className='flex items-center gap-2 mb-2'>
                <input
                  type='checkbox'
                  checked={formData.allTools}
                  onChange={e => handleChange('allTools', e.target.checked)}
                  className='rounded-lg border-slate-300'
                />
                <span className='text-sm text-slate-600'>Todas las herramientas</span>
              </label>
              {!formData.allTools && (
                <div className='grid grid-cols-2 md:grid-cols-3 gap-2'>
                  {TOOLS.map(tool => (
                    <label key={tool.value} className='flex items-center gap-2'>
                      <input
                        type='checkbox'
                        checked={formData.tools_enabled.includes(tool.value)}
                        onChange={() => handleToolToggle(tool.value)}
                        className='rounded-lg border-slate-300'
                      />
                      <span className='text-sm text-slate-600'>{tool.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className='block text-sm font-medium text-slate-700 mb-2'>Configuracion del contexto</label>

              {/* La memoria de casos: qué se hizo en situaciones parecidas. Va
                  arriba porque es lo que más cambia lo que el agente propone. */}
              <div className='mb-4'>
                <label className='block text-xs text-slate-500 mb-1'>Memoria de casos parecidos</label>
                <select
                  value={formData.memoria_de_casos}
                  onChange={e => handleChange('memoria_de_casos', e.target.value)}
                  className='w-full text-sm border border-slate-200 rounded-lg px-3 py-2'
                >
                  <option value='off'>Apagada</option>
                  <option value='mitad'>La mitad de los expedientes (para comparar)</option>
                  <option value='todo'>Siempre</option>
                </select>
                <p className='text-xs text-slate-500 mt-1'>
                  Le enseña qué contestamos y qué tareas creamos en momentos parecidos de otros
                  expedientes del mismo cliente. Con «la mitad» se puede comparar la tasa de
                  rechazo de los dos grupos en los mismos días.
                </p>
              </div>

              <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                <div>
                  <label className='block text-xs text-slate-500 mb-1'>Limite de interacciones</label>
                  <input
                    type='number'
                    value={formData.interactions_limit}
                    onChange={e => handleChange('interactions_limit', e.target.value)}
                    className='w-full text-sm border border-slate-200 rounded-lg px-3 py-2'
                    placeholder='20 por defecto'
                  />
                </div>
                <label className='flex items-center gap-2 self-end'>
                  <input
                    type='checkbox'
                    checked={formData.include_annotations}
                    onChange={e => handleChange('include_annotations', e.target.checked)}
                    className='rounded-lg border-slate-300'
                  />
                  <span className='text-sm text-slate-600'>Incluir anotaciones</span>
                </label>
                <label className='flex items-center gap-2 self-end'>
                  <input
                    type='checkbox'
                    checked={formData.include_documents}
                    onChange={e => handleChange('include_documents', e.target.checked)}
                    className='rounded-lg border-slate-300'
                  />
                  <span className='text-sm text-slate-600'>Incluir documentos</span>
                </label>
              </div>
            </div>

            <div className='flex justify-end gap-3 pt-4 border-t border-slate-200'>
              <button
                type='button'
                onClick={onClose}
                className='text-sm font-medium text-slate-600 hover:text-slate-800 px-4 py-2'
              >
                Cancelar
              </button>
              <button
                type='submit'
                disabled={saving}
                className='text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-lg px-4 py-2'
              >
                {saving ? 'Guardando...' : isEditing ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AIAgentPromptForm;
